import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClock, faUser, faPhone, faEnvelope, faComment, faInfoCircle, faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import emailjs from '@emailjs/browser';

const RendezVous: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis.';
    if (!formData.phone.trim()) newErrors.phone = 'Le téléphone est requis.';
    else if (!/^\+?\d{8,15}$/.test(formData.phone.trim().replace(/\s/g, ''))) newErrors.phone = 'Numéro invalide.';
    if (!formData.email.trim()) newErrors.email = 'L\'email est requis.';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email.trim())) newErrors.email = 'Email invalide.';
    if (!formData.date.trim()) newErrors.date = 'La date est requise.';
    if (!formData.time.trim()) newErrors.time = 'L\'heure est requise.';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    setLoading(true);

    // TODO: Replace with your actual EmailJS IDs
    const SERVICE_ID = 'service_m5bp2aa';
    const TEMPLATE_ID = 'template_bwipdyl';
    const PUBLIC_KEY = 'z0K2-fDMwlKmBdsul';

    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          date: formData.date,
          time: formData.time,
          message: formData.message,
        },
        PUBLIC_KEY
      );
      setLoading(false);
      setSuccess(true);
      setFormData({ name: '', phone: '', email: '', date: '', time: '', message: '' });
    } catch (err) {
      setLoading(false);
      alert("Erreur lors de l'envoi du formulaire");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div style={{ paddingTop: 'calc(5rem + var(--announcement-offset, 0px))' }}>
      {/* Hero Section */}
      <div className="bg-accent text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent to-black opacity-80"></div>
        <div className="relative z-10 section-container text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in-up">Prenez rendez-vous</h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
            Réservez votre consultation en ligne ou contactez-nous directement pour un rendez-vous.
          </p>
        </div>
      </div>

      {/* Appointment Form Section */}
      <div className="section-container contact-section-container py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch h-full">
          {/* Form */}
          <div className="animate-fade-in-left h-full flex flex-col">
            <div className="bg-gradient-to-br from-blue-50 via-white to-blue-100 rounded-2xl shadow-2xl p-10 border border-blue-100 w-full mx-auto animate-fade-in-up flex flex-col h-full">
              <h2 className="text-2xl font-bold text-accent mb-2 flex items-center gap-2 justify-center">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-primary" /> Formulaire de rendez-vous
              </h2>
              <p className="text-gray-500 text-center mb-6 text-sm">Remplissez le formulaire ci-dessous pour réserver votre consultation. Nous vous contacterons pour confirmer votre rendez-vous.</p>
              {success ? (
                <div className="flex flex-col items-center justify-center py-10">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-4xl mb-3 animate-bounce" />
                  <p className="text-green-700 text-lg font-semibold mb-2">Votre demande a été envoyée !</p>
                  <p className="text-gray-600 text-center">Nous vous contacterons très bientôt pour confirmer votre rendez-vous.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Nom complet */}
                  <div className="relative">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={`peer block w-full px-4 pt-6 pb-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-blue-300 transition-all duration-200 shadow-sm hover:border-primary ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
                      placeholder=" "
                      aria-invalid={!!errors.name}
                      aria-describedby="name-error"
                    />
                    <label htmlFor="name" className="absolute left-4 top-2 text-gray-500 text-sm transition-all duration-200 pointer-events-none peer-placeholder-shown:top-5 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:text-sm bg-white px-1">
                      <FontAwesomeIcon icon={faUser} className="mr-2 text-primary" /> Nom complet
                    </label>
                    <span className="text-xs text-gray-400 ml-1">Ex: Ayoub El Amrani</span>
                    {errors.name && <span id="name-error" className="block text-xs text-red-500 mt-1">{errors.name}</span>}
                  </div>
                  {/* Téléphone */}
                  <div className="relative">
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className={`peer block w-full px-4 pt-6 pb-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-blue-300 transition-all duration-200 shadow-sm hover:border-primary ${errors.phone ? 'border-red-400' : 'border-gray-300'}`}
                      placeholder=" "
                      aria-invalid={!!errors.phone}
                      aria-describedby="phone-error"
                    />
                    <label htmlFor="phone" className="absolute left-4 top-2 text-gray-500 text-sm transition-all duration-200 pointer-events-none peer-placeholder-shown:top-5 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:text-sm bg-white px-1">
                      <FontAwesomeIcon icon={faPhone} className="mr-2 text-primary" /> Téléphone
                    </label>
                    <span className="text-xs text-gray-400 ml-1">Format: +212 6 12 34 56 78</span>
                    {errors.phone && <span id="phone-error" className="block text-xs text-red-500 mt-1">{errors.phone}</span>}
                  </div>
                  {/* Email */}
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={`peer block w-full px-4 pt-6 pb-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-blue-300 transition-all duration-200 shadow-sm hover:border-primary ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
                      placeholder=" "
                      aria-invalid={!!errors.email}
                      aria-describedby="email-error"
                    />
                    <label htmlFor="email" className="absolute left-4 top-2 text-gray-500 text-sm transition-all duration-200 pointer-events-none peer-placeholder-shown:top-5 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:text-sm bg-white px-1">
                      <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-primary" /> Email
                    </label>
                    <span className="text-xs text-gray-400 ml-1">Votre email reste confidentiel</span>
                    {errors.email && <span id="email-error" className="block text-xs text-red-500 mt-1">{errors.email}</span>}
                  </div>
                  {/* Date & Heure */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                      <input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        className={`peer block w-full px-4 pt-6 pb-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-blue-300 transition-all duration-200 shadow-sm hover:border-primary ${errors.date ? 'border-red-400' : 'border-gray-300'}`}
                        placeholder=" "
                        aria-invalid={!!errors.date}
                        aria-describedby="date-error"
                      />
                      <label htmlFor="date" className="absolute left-4 top-2 text-gray-500 text-sm transition-all duration-200 pointer-events-none peer-placeholder-shown:top-5 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:text-sm bg-white px-1">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-primary" /> Date souhaitée
                      </label>
                      {errors.date && <span id="date-error" className="block text-xs text-red-500 mt-1">{errors.date}</span>}
                    </div>
                    <div className="relative">
                      <input
                        type="time"
                        id="time"
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        required
                        className={`peer block w-full px-4 pt-6 pb-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-blue-300 transition-all duration-200 shadow-sm hover:border-primary ${errors.time ? 'border-red-400' : 'border-gray-300'}`}
                        placeholder=" "
                        aria-invalid={!!errors.time}
                        aria-describedby="time-error"
                      />
                      <label htmlFor="time" className="absolute left-4 top-2 text-gray-500 text-sm transition-all duration-200 pointer-events-none peer-placeholder-shown:top-5 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:text-sm bg-white px-1">
                        <FontAwesomeIcon icon={faClock} className="mr-2 text-primary" /> Heure souhaitée
                      </label>
                      {errors.time && <span id="time-error" className="block text-xs text-red-500 mt-1">{errors.time}</span>}
                    </div>
                  </div>
                  {/* Message */}
                  <div className="relative">
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="peer block w-full px-4 pt-6 pb-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-blue-300 transition-all duration-200 shadow-sm hover:border-primary"
                      placeholder=" "
                    />
                    <label htmlFor="message" className="absolute left-4 top-2 text-gray-500 text-sm transition-all duration-200 pointer-events-none peer-placeholder-shown:top-5 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:text-sm bg-white px-1">
                      <FontAwesomeIcon icon={faComment} className="mr-2 text-primary" /> Motif de consultation
                    </label>
                    <span className="text-xs text-gray-400 ml-1">Précisez la raison de votre visite ou toute information supplémentaire</span>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-primary to-accent text-white hover:from-accent hover:to-primary font-semibold px-8 py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 text-lg shadow-md relative overflow-hidden disabled:opacity-60"
                    disabled={loading}
                  >
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin className="text-white text-xl" />
                    ) : (
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-white" />
                    )}
                    {loading ? 'Envoi en cours...' : 'Réserver le rendez-vous'}
                  </button>
                  <div className="w-full text-center mt-0">
                    <span className="text-xs text-accent font-semibold italic">Nous vous contacterons dans les plus brefs délais</span>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="flex justify-center animate-fade-in-right h-full">
            <div className="bg-white rounded-xl shadow-card p-8 w-full mx-auto animate-fade-in-up flex flex-col justify-between h-full">
              <h2 className="text-2xl font-bold text-accent mb-6 flex items-center gap-2 justify-center text-center">
                <FontAwesomeIcon icon={faInfoCircle} className="text-accent" /> Informations importantes
              </h2>
              <div className="flex-1 flex flex-col gap-8 py-2">
                {/* Horaires d'ouverture */}
                <div className="pb-4 border-b">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2 justify-center text-center">
                    <FontAwesomeIcon icon={faClock} className="text-primary" /> Horaires d'ouverture
                  </h3><br></br>
                  <p className="text-gray-600 text-center">
                    Lundi - Vendredi: 10h00 - 19h00<br />
                    Samedi: 10h00 - 14h00<br />
                    Dimanche: Fermé
                  </p>
                </div>
                {/* Durée des consultations */}
                <div className="pb-4 border-b">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2 justify-center text-center">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-primary" /> Durée des consultations approximative
                  </h3><br></br>
                  <ul className="text-gray-600 list-disc list-inside space-y-1 text-center">
                    <li>Consultation initiale: 15 - 30 minutes</li>
                    <li>Soins dentaires: 45-60 minutes</li>
                    <li>Séance orthodontique: 30-45 minutes</li>
                  </ul>
                </div>
                {/* Préparation */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2 justify-center text-center">
                    <FontAwesomeIcon icon={faUser} className="text-primary" /> Préparation
                  </h3>
                  <ul className="text-gray-600 list-disc list-inside space-y-1 mb-4 text-center">
                    <li>Arrivez 10 minutes avant votre rendez-vous</li>
                    <li>Apportez vos documents d'assurance si nécessaire</li>
                    <li>Précisez tout problème de santé ou allergie</li>
                  </ul>
                  <div className="flex flex-col items-center mt-4">
                    <div className="bg-yellow-50 border border-yellow-400 text-yellow-900 p-6 rounded-lg shadow-sm flex flex-col items-center w-full max-w-md mb-2">
                      <div className="flex items-center gap-2 mb-4">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-yellow-500 text-lg" />
                        <span className="font-bold text-base">Note importante</span>
                      </div>
                      <ul className="list-disc list-inside text-sm text-center space-y-2">
                        <li>Les patients mineurs doivent être accompagnés d'un parent ou tuteur légal</li>
                        <li>Les patients âgés présentant des difficultés ou des troubles doivent également être accompagnés</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RendezVous; 